import type { AppRole } from "@/contexts/AuthContext";

/**
 * One place to answer "is this person allowed to do this?".
 *
 * Front Desk sits outside the Viewer < Editor < Admin ladder. It is reception
 * and nursery administration work: nursery-wide, not tied to one room, but it
 * never covers hands-on care records (the medicine log), risk assessment
 * completion or sign-off, or LADO and social care records.
 */
export type Role = AppRole | string | null | undefined;

export const isViewer = (role: Role) => role === "viewer";
export const isFrontDesk = (role: Role) => role === "front_desk";
export const isAdminLevel = (role: Role) => role === "admin" || role === "super_admin";
export const isOwner = (role: Role) => role === "super_admin";

/** Room-based staff who can edit ongoing records: Editor, Admin, Owner. */
export const isEditorPlus = (role: Role) =>
  role === "editor" || isAdminLevel(role);

/* ── Register, attendance and club pickups ───────────────────────────── */

/** Sign children in and out, and tick club pickups and drop-offs. */
export const canSignChildrenInOut = (role: Role) =>
  isEditorPlus(role) || isViewer(role) || isFrontDesk(role);

/** Front Desk and Admin+ are not limited to their own room. */
export const isRoomScoped = (role: Role) => isViewer(role) || role === "editor";

/* ── Children ────────────────────────────────────────────────────────── */

/** See every child in the nursery, whatever room they are in. */
export const canSeeAllChildren = (role: Role) => isAdminLevel(role) || isFrontDesk(role);

/** Edit a child's contact, session, funding and SEND details. */
export const canEditChildDetails = (role: Role) => isEditorPlus(role) || isFrontDesk(role);

/** Both manual creation and enrolment conversion use the same child permissions. */
export const canAddChild = (role: Role) => isEditorPlus(role) || isFrontDesk(role);
export const canEnrolChild = canAddChild;

/** Spreadsheet import stays with Admin and Owner only. */
export const canImportChildren = (role: Role) => isAdminLevel(role);
/** Front Desk can archive leavers, but cannot restore them. */
export const canArchiveChild = (role: Role) => isAdminLevel(role) || isFrontDesk(role);

/** Restoring an archived child: Admin and Owner only. */
export const canUnarchiveChild = (role: Role) => isAdminLevel(role);

/* ── Care records ────────────────────────────────────────────────────── */

/**
 * The medicine log records a care act carried out by the person logging it,
 * so it is never an administrative task. Front Desk is excluded by design.
 */
export const canLogMedicine = (role: Role) => isEditorPlus(role);

/** Accident, incident, behaviour and complaint entries. */
export const canWriteSafeguardingLog = (role: Role) => isEditorPlus(role);

/* ── Risk assessments ────────────────────────────────────────────────── */

/** Front Desk can read risk assessments for reference. */
export const canViewRiskAssessments = (role: Role) => isEditorPlus(role) || isFrontDesk(role);

/** Completing one, and manager sign-off, stay with Editor and above. */
export const canCompleteRiskAssessment = (role: Role) => isEditorPlus(role);

/* ── Rota ────────────────────────────────────────────────────────────── */

/** Front Desk manages the rota for every room, like an Admin. */
export const canEditWholeRota = (role: Role) => isAdminLevel(role) || isFrontDesk(role);

/* ── Staff records ───────────────────────────────────────────────────── */

/** DBS and PFA expiry tracking, certificate uploads and renewal chasing. */
export const canManageCertifications = (role: Role) => isAdminLevel(role) || isFrontDesk(role);

/** Adding, editing or archiving a staff profile, and inviting people. */
export const canManageStaffProfiles = (role: Role) => isAdminLevel(role);

/* ── Additional Days ────────────────────────────────────────────────── */

export const canCreateAdditionalDayRequests = (role: Role) =>
  isEditorPlus(role) || isFrontDesk(role);
